import { Injectable } from "@angular/core";
import { FormFieldConfig } from "src/app/models/form-field-config.model";
import { ClaimService } from "src/app/services/claim/claim.service";
import { ConfigService } from "src/app/services/config/config.service";
import { I18nService } from "src/app/services/i18n/i18n.service";
import { PhoneValidationService } from "src/app/services/phone-validation/phone-validation.service";
import { of, Observable, combineLatest } from "rxjs";
import { map, withLatestFrom } from "rxjs/operators";
import { Store } from "@ngrx/store";
import { AppState } from "src/app/@store/reducers";
import { StsSelectors } from "src/app/@store/selectors/sts.selectors";
import { FormConfigInterface } from "src/app/form-configs/interfaces/form-config.interface";
export interface MyProfileSectionProps {
  visible: boolean;
  fields: FormFieldConfig[];
}

export interface MyProfileSection {
  [k: string]: MyProfileSectionProps;
}

@Injectable({ providedIn: "root" })
export class MyAccountService {
  private anon$ = this.store.select(StsSelectors.isAnonymousUser);
  private isForeigner$ = this.store.select(StsSelectors.isForeigner);
  private fiscalCode$ = this.store.select(StsSelectors.fiscalCode);
  private synlabAccessUser$ = this.store.select(StsSelectors.isSynlabAccessUser);
  private isHungaryTenant$ = this.store.select(StsSelectors.isHUTenant);
  private isFITenant$ = this.store.select(StsSelectors.isFITenant);
  private isITTenant$ = this.store.select(StsSelectors.isITTenant);
  private disabledFields$ = combineLatest([this.anon$, this.isHungaryTenant$]).pipe(
    map(([isAnon, isHU]) => {
      const disabledFieldsForAnonymousUser: string[] = ["given_name", "family_name", "email"];
      if (isHU && isAnon) return disabledFieldsForAnonymousUser;
      return ["email"];
    })
  );

  private hiddenFields$ = combineLatest([
    this.isForeigner$,
    this.fiscalCode$,
    this.isITTenant$,
  ]).pipe(
    map(([isForeigner, fiscalCode, isItaly]) => {
      const hiddenFieldsForItaly: string[] = ["fiscal_code"];
      if ((fiscalCode === null || isForeigner) && isItaly) return hiddenFieldsForItaly;

      return [];
    })
  );

  constructor(
    public i18nService: I18nService,
    public phoneValidator: PhoneValidationService,
    public claimService: ClaimService,
    private config: ConfigService,
    private store: Store<AppState>
  ) {}

  public get getProfileSection$(): Observable<MyProfileSection> {
    return combineLatest([
      this.isContactSectionVisible$,
      this.getFieldsForPersonalInfo$,
      this.getFieldsForContactInfo$,
    ]).pipe(
      map(([isVisible, personalFields, contactFields]) => {
        return {
          personal: {
            visible: true,
            fields: personalFields,
          },
          contact: {
            visible: isVisible,
            fields: contactFields,
          },
        };
      })
    );
  }

  private get isContactSectionVisible$() {
    // hide contact section for HU tenant and anonymous user
    return combineLatest([this.anon$, this.isHungaryTenant$]).pipe(
      map(([anon, isHu]) => {
        if (isHu && anon) return false;
        return true;
      })
    );
  }

  private get getFieldsForPersonalInfo$() {
    const formConfig = this.config?.formConfig || ({} as FormConfigInterface);
    const fields: FormFieldConfig[] = formConfig.personalInfo;
    return this.disabledFields$.pipe(
      withLatestFrom(this.hiddenFields$),
      map(([disabled, hiddenFields]) =>
      {
        const filtered = fields?.filter(field => !hiddenFields?.includes(field.Name))

        return filtered?.map((field) => ({
          ...field,
          Disabled: disabled.some((f) => f == field?.Name) || field.Readonly,
        }));
      }
      )
    );
  }

  private get getFieldsForContactInfo$() {
    return this.disabledFields$.pipe(
      withLatestFrom(this.isFITenant$, this.synlabAccessUser$),
      map(([disabled, isFi, isSynlabAccessUser]) => {
        const formConfig = this.config?.formConfig || ({} as FormConfigInterface);
        let fields: FormFieldConfig[] = formConfig.contact;

        if (isFi && !isSynlabAccessUser) {
          fields = formConfig.adminContact;
        }

        return fields?.map((field) => ({
          ...field,
          Disabled: disabled.some((f) => f == field?.Name) || field.Readonly,
        }));
      })
    );
  }
}
